import { useCallback, useEffect, useRef, useState } from 'react';

const EMPTY_TERMS = ['', ''];
const EMPTY_VISIBILITY = [false, false];
const DEFAULT_STATE = {
  searchTerm: EMPTY_TERMS,
  showSearchInput: EMPTY_VISIBILITY,
};

const useDualSearchInputs = ({
  debounceMs = 300,
  autoHideMs = 10000,
  controlledState,
  onStateChange,
} = {}) => {
  const [internalState, setInternalState] = useState(DEFAULT_STATE);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(EMPTY_TERMS);
  const hideTimersRef = useRef([null, null]);
  const inputRefs = useRef([]);
  const searchState = controlledState ?? internalState;
  const searchTerm = searchState.searchTerm ?? EMPTY_TERMS;
  const showSearchInput = searchState.showSearchInput ?? EMPTY_VISIBILITY;

  const updateSearchState = useCallback((updater) => {
    if (onStateChange) {
      onStateChange((prev) => {
        const baseState = prev ?? controlledState ?? DEFAULT_STATE;
        return typeof updater === 'function' ? updater(baseState) : updater;
      });
      return;
    }

    setInternalState((prev) => (
      typeof updater === 'function' ? updater(prev) : updater
    ));
  }, [controlledState, onStateChange]);

  const clearAutoHide = useCallback((idx) => {
    const timer = hideTimersRef.current[idx];
    if (timer) {
      clearTimeout(timer);
      hideTimersRef.current[idx] = null;
    }
  }, []);

  const focusInput = useCallback((idx) => {
    setTimeout(() => {
      inputRefs.current[idx]?.focus();
    }, 0);
  }, []);

  const startAutoHide = useCallback((idx) => {
    clearAutoHide(idx);
    hideTimersRef.current[idx] = setTimeout(() => {
      updateSearchState((prev) => {
        const updated = [...prev.showSearchInput];
        updated[idx] = false;
        return {
          ...prev,
          showSearchInput: updated,
        };
      });
      hideTimersRef.current[idx] = null;
    }, autoHideMs);
  }, [autoHideMs, clearAutoHide, updateSearchState]);

  const toggleSearchInput = useCallback((idx) => {
    updateSearchState((prev) => {
      const updated = [...prev.showSearchInput];

      if (updated[idx]) {
        updated[idx] = false;
        clearAutoHide(idx);
        const newTerms = [...prev.searchTerm];
        newTerms[idx] = '';
        return {
          ...prev,
          searchTerm: newTerms,
          showSearchInput: updated,
        };
      }

      updated[idx] = true;
      focusInput(idx);
      return {
        ...prev,
        showSearchInput: updated,
      };
    });
  }, [clearAutoHide, focusInput, updateSearchState]);

  const updateSearchTerm = useCallback((idx, value) => {
    updateSearchState((prev) => {
      const updated = [...prev.searchTerm];
      updated[idx] = value;
      return {
        ...prev,
        searchTerm: updated,
      };
    });
    if (value) {
      clearAutoHide(idx);
    }
  }, [clearAutoHide, updateSearchState]);

  const clearSearchTerm = useCallback((idx) => {
    updateSearchState((prev) => {
      const updated = [...prev.searchTerm];
      updated[idx] = '';
      return {
        ...prev,
        searchTerm: updated,
      };
    });
    focusInput(idx);
    startAutoHide(idx);
  }, [focusInput, startAutoHide, updateSearchState]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), debounceMs);
    return () => clearTimeout(id);
  }, [debounceMs, searchTerm]);

  useEffect(() => {
    showSearchInput.forEach((isVisible, idx) => {
      if (isVisible && !searchTerm[idx]) {
        startAutoHide(idx);
      } else {
        clearAutoHide(idx);
      }
    });
  }, [clearAutoHide, searchTerm, showSearchInput, startAutoHide]);

  useEffect(() => () => {
    hideTimersRef.current.forEach((timer) => timer && clearTimeout(timer));
    hideTimersRef.current = [null, null];
  }, []);

  return {
    debouncedSearchTerm,
    inputRefs,
    searchTerm,
    showSearchInput,
    toggleSearchInput,
    updateSearchTerm,
    clearSearchTerm,
  };
};

export default useDualSearchInputs;
